import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  firstValueFrom,
  map,
  startWith,
  Subject,
} from 'rxjs';
import {
  CreateWarehouseRequest,
  WarehouseRowConfig,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import {
  NotInput,
  NotButton,
  NotPhoneInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { CommonModule } from '@angular/common';
import { getErrorText, mobilePhoneValidator } from '@nebalkon-workspace/utils';
import {
  isWarehouseUuid,
  warehouseRouteSegment,
} from '../utils/warehouse-route.util';

const VALIDATION_PATTERNS = {
  INTEGER: /^\d+$/,
  DECIMAL: /^-?\d+(\.\d+)?$/,
  POSITIVE_DECIMAL: /^\d+(\.\d+)?$/,
} as const;

const FIELD_LIMITS = {
  NAME_MAX: 30,
  CITY_MAX: 30,
  ADDRESS_MAX: 60,
  SLUG_MAX: 255,
  MIN_LENGTH: 2,
} as const;

const SLUG_PATTERN = /^[a-z0-9-]+$/;

const COORDINATE_RANGES = {
  LATITUDE: { MIN: -90, MAX: 90 },
  LONGITUDE: { MIN: -180, MAX: 180 },
} as const;

interface WarehouseFormRowSnapshot {
  row_number: number;
  floors: number;
  columns: number;
}

interface WarehouseFormSnapshot {
  name: string;
  slug: string;
  city: string;
  address: string;
  phone: string;
  latitude: string;
  longitude: string;
  area_m2: string;
  height_m: string;
  useful_volume_m3: string;
  has_heating: boolean;
  camera_count: string;
  leak_sensor_count: string;
  rows: WarehouseFormRowSnapshot[];
}

function createCoordinateValidator(
  min: number,
  max: number,
  errorKey: string,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const num = Number(value);
    if (isNaN(num) || num < min || num > max) {
      return { [errorKey]: true };
    }
    return null;
  };
}

function positiveNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const num = Number(value);
    if (isNaN(num) || num <= 0) {
      return { invalidNumber: true };
    }
    return null;
  };
}

@Component({
  selector: 'lib-staff-warehouse-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    NotInput,
    NotPhoneInput,
    CabinetButtonComponent,
    NotButton,
    CommonModule,
  ],
  templateUrl: './staff-warehouse-form.component.html',
  styleUrl: './staff-warehouse-form.component.scss',
})
export class StaffWarehouseFormComponent implements OnInit {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #fb = inject(FormBuilder);
  readonly #warehouseService = inject(WarehouseService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly form: FormGroup;

  protected readonly isSubmitting = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly warehouseId = signal<string | null>(null);
  protected readonly warehouseSlug = signal<string | null>(null);
  protected readonly isEditMode = computed(() => !!this.warehouseId());
  protected readonly initialFormValue = signal<WarehouseFormSnapshot | null>(
    null,
  );
  protected readonly isFormChanged = signal(true);
  protected readonly unfocusedPhoneInput = new Subject<boolean>();
  protected readonly phoneControlErrorText;
  readonly #rowsVersion = signal(0);

  constructor() {
    this.form = this.#buildForm();

    this.phoneControlErrorText = toSignal(
      combineLatest([
        this.form.controls['phone'].valueChanges.pipe(startWith('')),
        this.unfocusedPhoneInput,
      ]).pipe(
        map(() =>
          getErrorText(this.form.controls['phone'], {
            invalidPhone: this.#translate.instant(
              'admin.warehouses.create.errors.invalidOperator',
            ),
            landlinePhone: this.#translate.instant(
              'admin.warehouses.create.errors.landlinePhone',
            ),
            invalidOperatorCode: this.#translate.instant(
              'admin.warehouses.create.errors.invalidOperator',
            ),
          }),
        ),
      ),
      { initialValue: '' },
    );
  }

  get rowsArray(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  #buildForm(): FormGroup {
    return this.#fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.NAME_MAX),
        ],
      ],
      slug: [
        '',
        [
          Validators.required,
          Validators.maxLength(FIELD_LIMITS.SLUG_MAX),
          Validators.pattern(SLUG_PATTERN),
        ],
      ],
      city: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.CITY_MAX),
        ],
      ],
      address: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.ADDRESS_MAX),
        ],
      ],
      phone: ['', [mobilePhoneValidator()]],
      latitude: [
        '',
        [
          createCoordinateValidator(
            COORDINATE_RANGES.LATITUDE.MIN,
            COORDINATE_RANGES.LATITUDE.MAX,
            'invalidLatitude',
          ),
          Validators.pattern(VALIDATION_PATTERNS.DECIMAL),
        ],
      ],
      longitude: [
        '',
        [
          createCoordinateValidator(
            COORDINATE_RANGES.LONGITUDE.MIN,
            COORDINATE_RANGES.LONGITUDE.MAX,
            'invalidLongitude',
          ),
          Validators.pattern(VALIDATION_PATTERNS.DECIMAL),
        ],
      ],
      area_m2: [
        '',
        [
          positiveNumberValidator(),
          Validators.pattern(VALIDATION_PATTERNS.POSITIVE_DECIMAL),
        ],
      ],
      height_m: [
        '',
        [
          positiveNumberValidator(),
          Validators.pattern(VALIDATION_PATTERNS.POSITIVE_DECIMAL),
        ],
      ],
      useful_volume_m3: [
        '',
        [
          positiveNumberValidator(),
          Validators.pattern(VALIDATION_PATTERNS.POSITIVE_DECIMAL),
        ],
      ],
      has_heating: [false],
      camera_count: [
        '',
        [Validators.min(0), Validators.pattern(VALIDATION_PATTERNS.INTEGER)],
      ],
      leak_sensor_count: [
        '',
        [Validators.min(0), Validators.pattern(VALIDATION_PATTERNS.INTEGER)],
      ],
      rows: this.#fb.array([]),
    });
  }

  public ngOnInit(): void {
    const slug = this.#route.snapshot.paramMap.get('slug');
    if (slug) {
      if (isWarehouseUuid(slug)) {
        this.warehouseId.set(slug);
        void this.#loadWarehouseData(slug);
      } else {
        void this.#loadWarehouseFromSlug(slug);
      }
    }

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const initialSnapshot = this.initialFormValue();
        if (this.isEditMode() && initialSnapshot) {
          const current = this.form.value as WarehouseFormSnapshot;
          this.isFormChanged.set(
            JSON.stringify(current) !== JSON.stringify(initialSnapshot),
          );
        }
      });

    this.rowsArray.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#rowsVersion.update((v) => v + 1);
        this.#cdr.markForCheck();
      });
  }

  protected getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (!control || !control.touched || !control.errors) {
      return '';
    }

    const errors = control.errors;

    if (errors['required']) {
      return 'admin.warehouses.create.errors.required';
    }
    if (errors['minlength']) {
      return 'admin.warehouses.create.errors.minLength';
    }
    if (errors['maxlength']) {
      const max = errors['maxlength'].requiredLength;
      return this.#translate.instant(
        'admin.warehouses.create.errors.maxLength',
        { max },
      );
    }
    if (errors['min']) {
      return 'admin.warehouses.create.errors.minValue';
    }
    if (errors['invalidLatitude']) {
      return 'admin.warehouses.create.errors.invalidLatitude';
    }
    if (errors['invalidLongitude']) {
      return 'admin.warehouses.create.errors.invalidLongitude';
    }
    if (errors['invalidNumber']) {
      return 'admin.warehouses.create.errors.invalidNumber';
    }
    if (errors['pattern']) {
      if (fieldName === 'slug') {
        return 'admin.warehouses.create.errors.slugInvalid';
      }
      return 'admin.warehouses.create.errors.invalidNumber';
    }

    return '';
  }

  protected addRow(): void {
    this.rowsArray.push(this.#createRowGroup(this.rowsArray.length + 1));
  }

  #createRowGroup(rowNumber: number): FormGroup {
    return this.#fb.group({
      row_number: [
        rowNumber,
        [
          Validators.required,
          Validators.min(1),
          Validators.pattern(VALIDATION_PATTERNS.INTEGER),
        ],
      ],
      floors: [
        1,
        [
          Validators.required,
          Validators.min(1),
          Validators.pattern(VALIDATION_PATTERNS.INTEGER),
        ],
      ],
      columns: [
        1,
        [
          Validators.required,
          Validators.min(1),
          Validators.pattern(VALIDATION_PATTERNS.INTEGER),
        ],
      ],
    });
  }

  protected removeRow(index: number): void {
    this.rowsArray.removeAt(index);
    this.#updateRowNumbers();
  }

  #updateRowNumbers(): void {
    this.rowsArray.controls.forEach((control, index) => {
      control.get('row_number')?.setValue(index + 1, { emitEvent: false });
    });
  }

  async #loadWarehouseFromSlug(slug: string): Promise<void> {
    this.warehouseSlug.set(slug);
    try {
      const warehouse = await firstValueFrom(
        this.#warehouseService.getWarehouseBySlug(slug),
      );
      this.warehouseId.set(warehouse.id);
      await this.#loadWarehouseData(warehouse.id);
    } catch (error) {
      console.error('Failed to resolve warehouse by slug:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.warehouses.edit.loadError')],
      });
      await this.#router.navigate([this.#cabinetPaths.staff, 'warehouses']);
    }
  }

  async #loadWarehouseData(id: string): Promise<void> {
    this.isLoading.set(true);

    try {
      const warehouse = await firstValueFrom(
        this.#warehouseService.getWarehouse(id),
      );

      if (warehouse) {
        this.warehouseSlug.set(warehouseRouteSegment(warehouse));

        const formValue = {
          name: warehouse.name,
          slug: warehouse.slug ?? '',
          city: warehouse.city,
          address: warehouse.address,
          phone: warehouse.phone ?? '',
          latitude: warehouse.latitude || '',
          longitude: warehouse.longitude || '',
          area_m2: warehouse.area_m2 || '',
          height_m: warehouse.height_m || '',
          useful_volume_m3: warehouse.useful_volume_m3 || '',
          has_heating: warehouse.has_heating || false,
          camera_count: warehouse.camera_count || '',
          leak_sensor_count: warehouse.leak_sensor_count || '',
        };

        this.form.patchValue(formValue);

        if (warehouse.rows && Array.isArray(warehouse.rows)) {
          warehouse.rows.forEach((row) => {
            const rowGroup = this.#createRowGroup(row.row_number);
            rowGroup.patchValue({
              floors: row.floors,
              columns: row.columns,
            });
            this.rowsArray.push(rowGroup);
          });
        }

        this.initialFormValue.set(
          structuredClone(this.form.value) as WarehouseFormSnapshot,
        );
        this.isFormChanged.set(false);
      } else {
        throw new Error('Warehouse not found');
      }
    } catch (error) {
      console.error('Failed to load warehouse:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.warehouses.edit.loadError')],
      });
      await this.#router.navigate([this.#cabinetPaths.staff, 'warehouses']);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async onSubmit(): Promise<void> {
    if (!this.#validateForm()) {
      return;
    }

    this.isSubmitting.set(true);

    try {
      const requestData = this.#prepareRequestData();
      await this.#saveWarehouse(requestData);
      const slug = this.warehouseSlug();
      if (this.isEditMode() && slug) {
        await this.#router.navigate([
          this.#cabinetPaths.staff,
          'warehouses',
          slug,
        ]);
      } else {
        await this.#router.navigate([this.#cabinetPaths.staff, 'warehouses']);
      }
    } catch (error) {
      this.#handleSubmitError(error);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  #validateForm(): boolean {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      this.unfocusedPhoneInput.next(true);
      return false;
    }
    return true;
  }

  #prepareRequestData(): CreateWarehouseRequest {
    const formValue = this.form.value as WarehouseFormSnapshot;
    const phone = (formValue.phone ?? '').trim();

    const requestData: CreateWarehouseRequest = {
      name: formValue.name.trim(),
      slug: formValue.slug.trim(),
      city: formValue.city.trim(),
      address: formValue.address.trim(),
      phone: phone || undefined,
      latitude: formValue.latitude ? Number(formValue.latitude) : undefined,
      longitude: formValue.longitude ? Number(formValue.longitude) : undefined,
      area_m2: formValue.area_m2 ? Number(formValue.area_m2) : undefined,
      height_m: formValue.height_m ? Number(formValue.height_m) : undefined,
      useful_volume_m3: formValue.useful_volume_m3
        ? Number(formValue.useful_volume_m3)
        : undefined,
      has_heating: formValue.has_heating,
      camera_count: formValue.camera_count
        ? Number(formValue.camera_count)
        : undefined,
      leak_sensor_count: formValue.leak_sensor_count
        ? Number(formValue.leak_sensor_count)
        : undefined,
    };

    if (formValue.rows?.length > 0) {
      requestData.rows = formValue.rows.map(
        (row): WarehouseRowConfig => ({
          row_number: Number(row.row_number),
          floors: Number(row.floors),
          columns: Number(row.columns),
        }),
      );
    }

    return requestData;
  }

  async #saveWarehouse(requestData: CreateWarehouseRequest): Promise<void> {
    if (this.isEditMode()) {
      const warehouseId = this.warehouseId();
      if (!warehouseId) {
        throw new Error('warehouse id is required in edit mode');
      }
      await firstValueFrom(
        this.#warehouseService.updateWarehouse(warehouseId, requestData),
      );

      this.warehouseSlug.set(requestData.slug ?? null);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.warehouses.edit.success')],
      });
    } else {
      const created = await firstValueFrom(
        this.#warehouseService.createWarehouse(requestData),
      );
      this.warehouseSlug.set(warehouseRouteSegment(created));

      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.warehouses.create.success')],
      });
    }
  }

  #handleSubmitError(error: unknown): void {
    console.error(
      `Failed to ${this.isEditMode() ? 'update' : 'create'} warehouse:`,
      error,
    );

    let message = this.#translate.instant(
      this.isEditMode()
        ? 'admin.warehouses.edit.error'
        : 'admin.warehouses.create.error',
    );
    if (error instanceof HttpErrorResponse && error.status === 409) {
      message = this.#translate.instant(
        'admin.warehouses.create.errors.slugExists',
      );
    }

    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [message],
    });
  }
}
